import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  HiEye, 
  HiHeart, 
  HiScale, 
  HiUsers, 
  HiTrendingUp 
} from 'react-icons/hi';

const MethodologySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const pillars = [
    {
      icon: HiEye,
      title: 'Self-awareness',
      description: 'Understanding your patterns, triggers, and leadership style to lead with authenticity.',
      span: 'md:col-span-8',
    },
    {
      icon: HiHeart,
      title: 'Emotional Intelligence',
      description: 'Developing the ability to recognize, understand, and manage emotions effectively.',
      span: 'md:col-span-4',
    },
    {
      icon: HiScale,
      title: 'Conscious Decision-making',
      description: 'Making choices aligned with values and long-term vision rather than reactive patterns.',
      span: 'md:col-span-4',
    },
    {
      icon: HiUsers,
      title: 'Team Alignment',
      description: 'Creating shared purpose and clarity to drive collective performance.',
      span: 'md:col-span-4',
      hasImage: true,
    },
    {
      icon: HiTrendingUp,
      title: 'Sustainable Performance',
      description: 'Building systems and habits that maintain high performance without burnout.',
      span: 'md:col-span-4',
    },
  ];

  return (
    <section
      id="methodology"
      ref={ref}
      data-testid="methodology-section"
      className="section-padding bg-[#FDFBF7]"
    >
      <div className="container-main">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="overline text-[#7A8B71] mb-4">Methodology</p>
          <h2 className="heading-2 text-[#0A192F] max-w-2xl" data-testid="methodology-headline">
            My Approach
          </h2>
          <p className="body-large text-[#4A5568] mt-6 max-w-3xl" data-testid="methodology-description">
            My work is built on a structured approach that integrates leadership development 
            with inner and organizational growth.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`bento-item ${pillar.span}`}
              data-testid={`methodology-pillar-${index + 1}`}
            >
              {pillar.hasImage ? (
                <div className="relative h-full min-h-[200px]">
                  <img
                    src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzQ5NzA3ODN8MA&ixlib=rb-4.1.0&q=85"
                    alt="Team collaboration"
                    className="absolute inset-0 w-full h-full object-cover rounded-lg"
                    data-testid="methodology-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 to-transparent rounded-lg" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="w-10 h-10 rounded-full bg-[#FDFBF7]/20 backdrop-blur flex items-center justify-center mb-3">
                      <pillar.icon className="w-5 h-5 text-[#FDFBF7]" />
                    </div>
                    <h3 className="text-xl font-medium text-[#FDFBF7]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {pillar.title}
                    </h3>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-[#7A8B71]/20 flex items-center justify-center mb-4">
                    <pillar.icon className="w-6 h-6 text-[#7A8B71]" />
                  </div>
                  <h3 className="heading-3 text-[#0A192F] mb-3">
                    {pillar.title}
                  </h3>
                  <p className="body-text text-[#4A5568]">
                    {pillar.description}
                  </p>
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MethodologySection;
